import dayjs from "dayjs";
import { cardTypeMap } from "./mapping";

export const prepareValuesToApi = (obj) => {

    if (typeof obj === 'object' && Array.isArray(obj)) {
        return obj.map(entry => prepareValuesToApi(entry));
    }

    if (typeof obj !== "object" || obj === null) {
        return obj;
    }

    let newObj = { ...obj };
    for (let key in obj) {
        if (newObj.hasOwnProperty(key)) {
            // dayjs.isDayjs(...), not `instanceof dayjs` — dayjs is a factory
            // function, not a class, so instanceof against it doesn't work.
            // (This replaced an `instanceof moment` check during the
            // moment -> dayjs migration.)
            if (dayjs.isDayjs(newObj[key])) {
                if (newObj[key].format("HH:mm:ss") === "00:00:00") {
                    newObj[key] = newObj[key].format("YYYY-MM-DD");
                }
                else {
                    newObj[key] = newObj[key].format("YYYY-MM-DD HH:mm:ss");
                }
            }
        }
    }
    return newObj;
}

export const snakeToCamel = (obj) => {

    if (typeof obj === 'object' && Array.isArray(obj)) {
        return obj.map(entry => snakeToCamel(entry));
    }

    if (typeof obj !== "object" || obj === null) {
        return obj;
    }

    let newObj = {};
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            let newKey = key.replace(/_([a-z])/g, (match) => {
                return match[1].toUpperCase();
            });
            newObj[newKey] = (typeof obj[key] === 'object') ? snakeToCamel(obj[key]) : obj[key];
        }
    }
    return newObj;
}

export const camelToSnake = (obj) => {
    if (typeof obj === 'object' && Array.isArray(obj)) {
        return obj.map(entry => camelToSnake(entry));
    }

    if (typeof obj !== "object" || obj === null) {
        return obj;
    }

    let newObj = {};
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            let newKey = key.replace(/([A-Z])/g, (match) => {
                return "_" + match.toLowerCase();
            });
            newObj[newKey] = (typeof obj[key] === 'object') ? camelToSnake(obj[key]) : obj[key];
        }
    }
    return newObj;
}

export const formatNumberAmount = (val) => {
    return val.toLocaleString(
        'pt-PT',
        { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }
    );
}

export const formatDateRange = (minDate, maxDate) => {
    return minDate !== null && maxDate !== null ? (
        `De ${minDate.format("YYYY-MM-DD")} a ${maxDate.format("YYYY-MM-DD")}`
    ) : maxDate !== null ? (
        `Até ${maxDate.format("YYYY-MM-DD")}`
    ) : minDate !== null ? (
        `Desde ${minDate.format("YYYY-MM-DD")}`
    ) : "Sem datas associadas";
}

export const formatFullAddress = (address, postCode, town) => {
    const addressFmt = Boolean(address) ? `${address}, ` : "";
    return `${addressFmt}${postCode} ${town}`;
}

export const formatFullEmail = (email, paymentEmail) => {
    if (Boolean(email) && Boolean(paymentEmail) && email !== paymentEmail) return `${email} / ${paymentEmail}`;
    if (Boolean(email) && Boolean(paymentEmail)) return email;
    if (Boolean(email)) return `${email} (email de pagamento em falta)`;
    if (Boolean(paymentEmail)) return `${paymentEmail} (email de pagamento)`;
    return "-";
}

export const formatDocumentType = (idCardType) => {
    const cardTypeOptions = cardTypeMap().reduce((obj, item) => { obj[item.id] = item.label; return obj; }, {});
    return Boolean(idCardType) ? cardTypeOptions[idCardType] : "Documento de Identificação";
}

export const formatDocumentNumber = (identificationNumber, idCardValidity) => {
    return Boolean(identificationNumber) ? (
        `${identificationNumber} ${Boolean(idCardValidity) ? ` (válido até ${dayjs(idCardValidity).format("YYYY-MM-DD")})` : ""}`
    ) : null;
}

export const formatTelephoneNumbers = (phoneNrMobilePrimary, phoneNrMobileSecondary, phoneNrHome = null, phoneNrWork = null) => {
    return [phoneNrMobilePrimary, phoneNrMobileSecondary, phoneNrHome, phoneNrWork].filter((value) => Boolean(value)).join(" | ");
}

export const formatFullBirthDate = (birthDate) => {
    const age = Boolean(birthDate) ? dayjs().diff(dayjs(birthDate), 'years') : null;
    const fmtBirthDate = Boolean(birthDate) ? dayjs(birthDate).format("YYYY-MM-DD") : null;
    return Boolean(fmtBirthDate) ? `${fmtBirthDate} (${age} anos)` : "-";
}

export const groupGames = (games) => {
    // Group the games by date
    const groupByDate = games.reduce((acc, game) => {
        const date = dayjs(game.date).format("YYYY-MM-DD");
        if (!acc[date]) { acc[date] = []; }
        acc[date].push(game);
        return acc;
    }, {});

    // Sort the dates array to ensure they are in chronological order
    const dates = Object.keys(groupByDate);
    if (dates.length === 0) { return {groupedGames: [], mainGroupIndex: -1}; }
    dates.sort();

    // Initialize an array to hold the groups
    let groups = [];

    // Initialize variables to hold the current group information
    let currentGroup = {
        start: dates[0],
        end: dates[0],
        dates: [dates[0]],
    };

    // Function to check if two dates are adjacent
    const areDatesAdjacent = (date1, date2) => {
        const diffInTime = new Date(date2) - new Date(date1);
        return diffInTime === 86400000; // 86400000 ms in a day
    }

    // Iterate through the dates array starting from the second element
    for (let i = 1; i < dates.length; i++) {
        const currentDate = dates[i];
        const previousDate = dates[i - 1];

        // Check if the current date is adjacent to the previous date
        if (areDatesAdjacent(previousDate, currentDate)) {
            // If they are adjacent, update the current group's end date and add the date to the dates array
            currentGroup.end = currentDate;
            currentGroup.dates.push(currentDate);
        } else {
            // If they are not adjacent, push the current group to the groups array and start a new group
            groups.push(currentGroup);
            currentGroup = {
                start: currentDate,
                end: currentDate,
                dates: [currentDate],
            };
        }
    }
  
    // Push the last group to the groups array
    groups.push(currentGroup);

    let mainGroupIndex = -1;
    let lastStart = null;

    // Add the games to the groups
    const groupedGames = groups.map((group, groupIdx) => {

        group["games"] = group.dates.map(date => {
            return {
                date,
                games: groupByDate[date].sort((a, b) => { return new Date(a.date) - new Date(b.date); }),
            };
        });

        const current = dayjs();
        const distanceToStart = dayjs(group.start).diff(current, "days");
        const isBetween = current.isBetween(group.start, group.end, null, "[]");
        if (isBetween) {
            mainGroupIndex = groupIdx;
        }
        else {
            if (mainGroupIndex === -1 && distanceToStart > 0) {
                if (lastStart === null) {
                    lastStart = group.start;
                    mainGroupIndex = groupIdx;
                }
            }
        }

        delete group.dates;
        return group;
    });

    if (mainGroupIndex === -1) {
        mainGroupIndex = groups.length - 1;
    }

    return { groupedGames, mainGroupIndex };
}
